import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActionItemModel } from '@src/app/modules/actions';
import { Nullable } from '@src/types/utils';
import { ChatTypeEnum } from '@src/api-messenger';

import { ChatModel, ChatPermissionsModel } from '../types';
import { DialogService } from '../../services';
import { EditDialogService } from '../chat-edit-form';

@Component({
  selector: 'chat-header',
  templateUrl: './chat-header.component.html',
  styleUrls: ['./chat-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ChatHeaderComponent {
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly editDialogService = inject(EditDialogService);

  @Input() chat?: ChatModel;
  @Input() permissions?: Nullable<ChatPermissionsModel>;
  @Input() visibleDetails = false;
  @Output() visibleDetailsChange = new EventEmitter<boolean>();
  @Output() muteChange = new EventEmitter<boolean>();
  @Output() delete = new EventEmitter<void>();
  @Output() leave = new EventEmitter<void>();

  actionMenuOpened = false;
  actionMenuItems: ActionItemModel[] = [
    {
      title: this.translateService.instant('components.telegramChat.buttons.actionMenuMute'),
      icon: '@tui.bell-off',
      visible: () => {
        return !!this.chat && !this.chat.settings?.muted;
      },
      action: () => this.muteChange.emit(true),
    },
    {
      title: this.translateService.instant('components.telegramChat.buttons.actionMenuUnmute'),
      icon: '@tui.bell',
      visible: () => {
        return !!this.chat && !!this.chat.settings?.muted;
      },
      action: () => this.muteChange.emit(false),
    },
    {
      title: this.translateService.instant('common.buttons.edit'),
      icon: '@tui.edit',
      visible: () => {
        return !!this.permissions?.canEditChat;
      },
      action: () => this.openEditDialog(),
    },
    {
      title: this.translateService.instant('components.internalMessenger.buttons.leaveChat'),
      icon: '@tui.log-out',
      visible: () => {
        // TODO: Проверить сценарий для разных админов (если вышли все админы)
        return !this.permissions?.isChatAdmin && !!this.permissions?.canLeaveChat;
      },
      action: () => this.openConfirmLeaveDialog(),
    },
    {
      title: this.translateService.instant('components.internalMessenger.buttons.deleteChat'),
      icon: '@tui.trash-2',
      color: 'var(--int-mes-color-red)',
      visible: () => {
        return !!this.permissions?.canDeleteChat;
      },
      action: () => this.openConfirmDeleteDialog(),
    },
  ];

  get detailsButtonVisible() {
    return this.permissions?.isChatAdmin || this.chat?.chatType !== ChatTypeEnum.PublicChat;
  }

  onClickDetailsButton() {
    this.visibleDetails = !this.visibleDetails;
    this.visibleDetailsChange.emit(this.visibleDetails);
  }

  openConfirmDeleteDialog(): void {
    this.dialogService.confirmDeleteChatDialog.subscribe({
      next: res => {
        if (res) {
          this.delete.emit();
        }
      },
    });
  }

  openConfirmLeaveDialog(): void {
    this.dialogService.confirmLeaveChatDialog.subscribe({
      next: res => {
        if (res) {
          this.leave.emit();
        }
      },
    });
  }

  openEditDialog(): void {
    if (!this.chat) {
      return;
    }

    this.editDialogService.open('Редактирование чата', this.chat).subscribe();
  }
}
